import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  ClipboardCheck,
  ClipboardList,
  DoorOpen,
  FileCheck2,
  FileSearch,
  FlaskConical,
  HardHat,
  Inbox,
  LayoutDashboard,
  Plus,
  Settings,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { quickSearch } from '../../api/buildings.js';


interface Action {
  label: string;
  hint?: string;
  icon: typeof Building2;
  onSelect: () => void;
  shortcut?: string;
}

/**
 * Phase 1 command palette: nav actions, quick-create actions, and live
 * search across buildings + units (debounced). Later phases plug in
 * work orders, vendors, and compliance records using the same shape.
 *
 * The ⌘K listener is attached at the layout level so it works on every
 * authed route.
 */
export function OrgCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const searchQuery = useQuery({
    queryKey: ['quick-search', debouncedQuery],
    queryFn: () => quickSearch({ q: debouncedQuery, limit: 8 }),
    enabled: debouncedQuery.length >= 2 && open,
    staleTime: 30_000,
  });

  function go(to: string) {
    return () => {
      setOpen(false);
      navigate(to);
    };
  }

  const navActions: Action[] = [
    { label: 'Today', icon: LayoutDashboard, onSelect: go('/dashboard'), shortcut: 'G D' },
    { label: 'Inbox', icon: Inbox, onSelect: go('/inbox'), shortcut: 'G N' },
    { label: 'Buildings', icon: Building2, onSelect: go('/buildings'), shortcut: 'G B' },
    { label: 'Work orders', icon: ClipboardList, onSelect: go('/work-orders'), shortcut: 'G W' },
    { label: 'My open work orders', icon: ClipboardList, onSelect: go('/work-orders?mine=1') },
    { label: 'Compliance', icon: ClipboardCheck, onSelect: go('/compliance'), shortcut: 'G C' },
    { label: 'Inspections', icon: FileSearch, onSelect: go('/inspections'), shortcut: 'G I' },
    { label: 'Inspection templates', icon: FileCheck2, onSelect: go('/inspections/templates') },
    { label: 'Inspection schedules', icon: FileSearch, onSelect: go('/inspections/schedules') },
    { label: 'Violations', icon: AlertTriangle, onSelect: go('/violations'), shortcut: 'G V' },
    { label: 'Open violations board', icon: AlertTriangle, onSelect: go('/violations') },
    { label: 'Lead tracking', icon: FlaskConical, onSelect: go('/lead'), shortcut: 'G L' },
    { label: 'Lead positives across portfolio', icon: FlaskConical, onSelect: go('/lead') },
    { label: 'Profile settings', icon: Settings, onSelect: go('/settings/profile') },
    { label: 'Security', icon: ShieldCheck, onSelect: go('/settings/security') },
    { label: 'Notifications', icon: Settings, onSelect: go('/settings/notifications') },
    { label: 'Branding', icon: Building2, onSelect: go('/settings/branding') },
    { label: 'Users', icon: HardHat, onSelect: go('/settings/users') },
    { label: 'Teams', icon: HardHat, onSelect: go('/settings/teams') },
  ];

  const createActions: Action[] = [
    { label: 'New work order', icon: Plus, onSelect: go('/work-orders?new=1') },
    { label: 'New inspection', icon: Plus, onSelect: go('/inspections/runs') },
    { label: 'New inspection schedule', icon: Plus, onSelect: go('/inspections/schedules') },
    { label: 'Add building', icon: Plus, onSelect: go('/buildings/new') },
    { label: 'Import buildings (CSV)', icon: Upload, onSelect: go('/buildings/import') },
  ];

  const stubs: Action[] = [
    {
      label: 'Search vendors (coming in Phase 3)',
      icon: HardHat,
      onSelect: () => setOpen(false),
    },
  ];

  const showResults = debouncedQuery.length >= 2;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search work orders, buildings, units, or jump to…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searchQuery.isFetching ? 'Searching…' : 'No matches.'}
        </CommandEmpty>

        {showResults && (searchQuery.data?.buildings?.length ?? 0) > 0 ? (
          <CommandGroup heading="Buildings">
            {searchQuery.data!.buildings.map((b) => (
              <CommandItem
                key={`b-${b.id}`}
                value={`b ${b.name} ${b.addressLine1}`}
                onSelect={go(`/buildings/${b.id}`)}
              >
                <Building2 className="h-4 w-4 text-fg-muted" />
                <span className="flex-1 truncate">
                  <span className="font-medium">{b.name}</span>{' '}
                  <span className="text-fg-muted">— {b.addressLine1}, {b.city}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {showResults && (searchQuery.data?.units?.length ?? 0) > 0 ? (
          <CommandGroup heading="Units">
            {searchQuery.data!.units.map((u) => (
              <CommandItem
                key={`u-${u.id}`}
                value={`u ${u.unitNumber} ${u.buildingName}`}
                onSelect={go(`/units/${u.id}`)}
              >
                <DoorOpen className="h-4 w-4 text-fg-muted" />
                <span className="flex-1 truncate">
                  Unit {u.unitNumber}{' '}
                  <span className="text-fg-muted">in {u.buildingName}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {showResults && (searchQuery.data?.workOrders?.length ?? 0) > 0 ? (
          <CommandGroup heading="Work orders">
            {searchQuery.data!.workOrders.map((w) => (
              <CommandItem
                key={`w-${w.id}`}
                value={`w ${w.displayNumber} ${w.title}`}
                onSelect={go(`/work-orders/${w.id}`)}
              >
                <ClipboardList className="h-4 w-4 text-fg-muted" />
                <span className="flex-1 truncate">
                  <span className="font-mono text-xs text-fg-muted">{w.displayNumber}</span>{' '}
                  <span>{w.title}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {showResults && (searchQuery.data?.inspectionTemplates?.length ?? 0) > 0 ? (
          <CommandGroup heading="Inspection templates">
            {searchQuery.data!.inspectionTemplates.map((t) => (
              <CommandItem
                key={`it-${t.id}`}
                value={`it ${t.name}`}
                onSelect={go(`/inspections/templates/${t.id}`)}
              >
                <FileCheck2 className="h-4 w-4 text-fg-muted" />
                <span className="flex-1 truncate">
                  <span className="font-medium">{t.name}</span>{' '}
                  <span className="text-fg-muted">({t.status})</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {showResults && (searchQuery.data?.inspections?.length ?? 0) > 0 ? (
          <CommandGroup heading="Inspections">
            {searchQuery.data!.inspections.map((i) => (
              <CommandItem
                key={`i-${i.id}`}
                value={`i ${i.id}`}
                onSelect={go(`/inspections/runs/${i.id}`)}
              >
                <FileSearch className="h-4 w-4 text-fg-muted" />
                <span className="flex-1 truncate">
                  <span className="font-mono text-xs">{i.id.slice(0, 8)}</span>{' '}
                  <span className="text-fg-muted">{i.status}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {showResults ? <CommandSeparator /> : null}

        <CommandGroup heading="Create">
          {createActions.map((action) => (
            <CommandItem key={action.label} onSelect={action.onSelect}>
              <action.icon className="h-4 w-4 text-fg-muted" />
              {action.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigate">
          {navActions.map((action) => (
            <CommandItem key={action.label} onSelect={action.onSelect}>
              <action.icon className="h-4 w-4 text-fg-muted" />
              {action.label}
              {action.shortcut ? <CommandShortcut>{action.shortcut}</CommandShortcut> : null}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Search">
          {stubs.map((stub) => (
            <CommandItem key={stub.label} onSelect={stub.onSelect}>
              <stub.icon className="h-4 w-4 text-fg-muted" />
              {stub.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

interface TriggerProps {
  onClick: () => void;
}

/** Ghost button + ⌘K hint placed in the top bar. */
export function CommandPaletteTrigger({ onClick }: TriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 w-full max-w-[360px] items-center justify-between gap-2 rounded-[10px] border border-border bg-surface px-3 text-sm text-fg-muted transition-colors hover:border-border-strong hover:text-fg"
    >
      <span className="truncate">Search buildings, units, work orders…</span>
      <kbd className="hidden text-xs text-fg-muted md:inline-flex items-center gap-0.5 rounded bg-surface-muted px-1.5 py-0.5">
        ⌘K
      </kbd>
    </button>
  );
}
